import { useState, useCallback } from 'react';
import { MotionProps } from 'motion/react';

interface useImageLoaderProps {
  src?: string;
  isHighPriority?: boolean;
}

export interface ImageLoaderReturn {
  /** Props to spread on motion.img */
  motionProps: MotionProps;
  /** Standard img props for non-motion usage */
  imgProps: React.ImgHTMLAttributes<HTMLImageElement>;
  /** Whether the image has loaded */
  isLoaded: boolean;
  /** Whether there was an error loading */
  isError: boolean;
  /** Motion animate state for image */
  imageAnimate: { opacity: number };
  /** Motion animate state for placeholder */
  placeholderAnimate: { opacity: number };
}

/**
 * Image optimization hook designed for motion animations
 */
export const useImageLoader = ({
  src,
  isHighPriority = false,
}: useImageLoaderProps): ImageLoaderReturn => {
  // High priority images start visible, others start hidden
  const [isLoaded, setIsLoaded] = useState(isHighPriority);
  const [isError, setIsError] = useState(false);

  // Update state when image loads
  const handleLoad = useCallback(() => {
    setIsLoaded(true);
  }, []);

  const handleError = useCallback(() => {
    setIsError(true);
    setIsLoaded(false);
  }, []);

  // If no src, early return
  if (!src) {
    return {
      motionProps: {
        initial: { opacity: 1 },
        animate: { opacity: 1 },
      },
      imgProps: {} as React.ImgHTMLAttributes<HTMLImageElement>,
      isLoaded: true,
      isError: false,
      imageAnimate: { opacity: 1 },
      placeholderAnimate: { opacity: 0 },
    };
  }

  // Base image props
  const imgProps: React.ImgHTMLAttributes<HTMLImageElement> = {
    src,
    loading: isHighPriority ? 'eager' : 'lazy',
    fetchPriority: isHighPriority ? 'high' : 'auto',
    onLoad: handleLoad,
    onError: handleError,
  };

  // Motion specific props
  const motionProps: MotionProps = {
    initial: { opacity: 0 },
    animate: { opacity: isLoaded ? 1 : 0 },
    transition: {
      duration: 0.3,
      ease: 'easeOut',
    },
  };

  // Animation states for external use
  const imageAnimate = { opacity: isLoaded ? 1 : 0 };
  const placeholderAnimate = { opacity: isLoaded || isError ? 0 : 1 };

  return {
    motionProps,
    imgProps,
    isLoaded,
    isError,
    imageAnimate,
    placeholderAnimate,
  };
};
